type BuildMetadata = {
  build_sha: string;
  build_time_utc?: string;
  schema_version?: number;
};

type StoredBuildState = {
  build_sha: string;
  build_time_utc: string;
  seen_at: string;
  schema_version: 1;
};

type LegacyReloadMarker = {
  target_build_sha: string;
  from_build_sha: string;
  attempted_at: number;
};

type ReloadTargetLedger = {
  version: 2;
  attempted_target_build_shas: string[];
};

type ReloadClaimResult =
  | { status: 'claimed' }
  | { status: 'already_attempted' }
  | { status: 'capacity_reached' }
  | { status: 'storage_unavailable' }
  | { status: 'storage_malformed' }
  | { status: 'storage_unverified' };

type VersionGuardTelemetryEventName = 'cleanup' | 'reload_requested' | 'reload_suppressed';

type VersionGuardTelemetryEvent = {
  event: VersionGuardTelemetryEventName;
  at: string;
  current_build_sha: string;
  current_build_time_utc: string;
  reason: string;
  from_build_sha?: string;
  to_build_sha?: string;
  server_build_sha?: string;
  local_storage_keys_removed?: string[];
  cache_storage_keys_removed?: string[];
  service_workers_unregistered?: number;
  recovery_reason?: Exclude<ReloadClaimResult['status'], 'claimed'>;
};

type VersionCleanupResult = {
  localStorageKeysRemoved: string[];
  cacheStorageKeysRemoved: string[];
  serviceWorkersUnregistered: number;
};

declare global {
  interface Window {
    __MG_CLIENT_VERSION_GUARD_EVENTS__?: VersionGuardTelemetryEvent[];
  }
}

export type ClientVersionGuardResult = {
  reloaded: boolean;
  currentBuildSha: string;
  serverBuildSha?: string;
  cleanupReason?: string;
  recoveryRequired?: boolean;
};

const CURRENT_BUILD_SHA = import.meta.env.VITE_BUILD_SHA ?? 'unknown';
const CURRENT_BUILD_TIME_UTC = import.meta.env.VITE_BUILD_TIME_UTC ?? '';
const BUILD_STATE_KEY = 'mg_client_build_contract_v1';
const RELOAD_MARKER_KEY = 'mg_client_build_reload_marker_v1';
const RELOAD_TARGET_LEDGER_VERSION = 2;
const MAX_ATTEMPTED_RELOAD_TARGETS = 8;
const VERSION_CHECK_TIMEOUT_MS = 2_000;
const WATCH_INTERVAL_MS = 5 * 60_000;
const TELEMETRY_EVENT_STORE_LIMIT = 50;

const VERSION_SENSITIVE_LOCAL_KEYS = [
  'mg_feed_cache_v1',
  'mg_ticket_cache_light_v1',
  'mg_reference_snapshot_v1',
  'mg_operator_splitter_sizes',
  'mg_operator_view_mode',
  'mg_messages_center_tab',
  'mg_ticket_fields_visible',
  'mg_ticket_fields_order',
  'mg_feed_font_styles',
  'mg_ticket_card_font_styles',
  'mg_tickets_filters_visible',
  'mg_tickets_filters_visible_order',
  'mg_tickets_columns_visible',
  'mg_tickets_columns_visible_order',
];

const VERSION_SENSITIVE_LOCAL_PREFIXES = [
  'mg_feed_mobile_ui:',
  'mg_feed_visual_style:',
];

let watchInstalled = false;
let checkInFlight: Promise<ClientVersionGuardResult> | null = null;

const isBrowser = (): boolean => typeof window !== 'undefined';

const normalizeBuildSha = (value: unknown): string => {
  const normalized = String(value ?? '').trim();
  return normalized || 'unknown';
};

const readStoredBuildState = (): StoredBuildState | null => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(BUILD_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredBuildState>;
    const buildSha = normalizeBuildSha(parsed.build_sha);
    if (buildSha === 'unknown') return null;
    return {
      build_sha: buildSha,
      build_time_utc: String(parsed.build_time_utc ?? ''),
      seen_at: String(parsed.seen_at ?? ''),
      schema_version: 1,
    };
  } catch {
    return null;
  }
};

const writeStoredBuildState = (metadata: BuildMetadata): void => {
  if (!isBrowser()) return;
  const state: StoredBuildState = {
    build_sha: normalizeBuildSha(metadata.build_sha),
    build_time_utc: String(metadata.build_time_utc ?? CURRENT_BUILD_TIME_UTC),
    seen_at: new Date().toISOString(),
    schema_version: 1,
  };
  try {
    window.localStorage.setItem(BUILD_STATE_KEY, JSON.stringify(state));
  } catch {
    // Storage failures must not block app startup.
  }
};

const readReloadTargetLedger = (): {
  status: 'empty' | 'valid' | 'storage_unavailable' | 'storage_malformed';
  targets: string[];
} => {
  if (!isBrowser()) return { status: 'storage_unavailable', targets: [] };
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
  } catch {
    return { status: 'storage_unavailable', targets: [] };
  }
  if (!raw) return { status: 'empty', targets: [] };
  try {
    const parsed = JSON.parse(raw) as Partial<ReloadTargetLedger & LegacyReloadMarker>;
    if (parsed.version === RELOAD_TARGET_LEDGER_VERSION) {
      if (!Array.isArray(parsed.attempted_target_build_shas)) {
        return { status: 'storage_malformed', targets: [] };
      }
      const targets = parsed.attempted_target_build_shas.map(normalizeBuildSha);
      if (
        targets.some((target) => target === 'unknown')
        || new Set(targets).size !== targets.length
        || targets.length > MAX_ATTEMPTED_RELOAD_TARGETS
      ) {
        return { status: 'storage_malformed', targets: [] };
      }
      return { status: 'valid', targets };
    }

    // Version 1 stored a single target plus its attempt timestamp. Keep a
    // valid legacy claim indefinitely rather than reviving the old TTL loop.
    const targetBuildSha = normalizeBuildSha(parsed.target_build_sha);
    const attemptedAt = Number(parsed.attempted_at);
    if (targetBuildSha === 'unknown' || !Number.isFinite(attemptedAt)) {
      return { status: 'storage_malformed', targets: [] };
    }
    return { status: 'valid', targets: [targetBuildSha] };
  } catch {
    return { status: 'storage_malformed', targets: [] };
  }
};

const claimReloadTarget = (targetBuildSha: string): ReloadClaimResult => {
  const target = normalizeBuildSha(targetBuildSha);
  const ledger = readReloadTargetLedger();
  if (ledger.status === 'storage_unavailable') return { status: 'storage_unavailable' };
  if (ledger.status === 'storage_malformed') return { status: 'storage_malformed' };
  if (ledger.targets.includes(target)) return { status: 'already_attempted' };
  if (ledger.targets.length >= MAX_ATTEMPTED_RELOAD_TARGETS) return { status: 'capacity_reached' };

  const next: ReloadTargetLedger = {
    version: RELOAD_TARGET_LEDGER_VERSION,
    attempted_target_build_shas: [...ledger.targets, target],
  };
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, JSON.stringify(next));
  } catch {
    return { status: 'storage_unavailable' };
  }

  const readBack = readReloadTargetLedger();
  if (readBack.status !== 'valid' || !readBack.targets.includes(target)) {
    return { status: 'storage_unverified' };
  }
  return { status: 'claimed' };
};

const emitVersionGuardTelemetry = (
  event: VersionGuardTelemetryEventName,
  payload: Omit<VersionGuardTelemetryEvent, 'event' | 'at' | 'current_build_sha' | 'current_build_time_utc'>,
): void => {
  if (!isBrowser()) return;
  const detail: VersionGuardTelemetryEvent = {
    event,
    at: new Date().toISOString(),
    current_build_sha: normalizeBuildSha(CURRENT_BUILD_SHA),
    current_build_time_utc: CURRENT_BUILD_TIME_UTC,
    ...payload,
  };
  try {
    const current = window.__MG_CLIENT_VERSION_GUARD_EVENTS__ ?? [];
    current.push(detail);
    if (current.length > TELEMETRY_EVENT_STORE_LIMIT) {
      current.splice(0, current.length - TELEMETRY_EVENT_STORE_LIMIT);
    }
    window.__MG_CLIENT_VERSION_GUARD_EVENTS__ = current;
  } catch {
    // telemetry must never block app startup
  }
  try {
    window.dispatchEvent(new CustomEvent('mg:client-version-guard', { detail }));
  } catch {
    // telemetry must never block app startup
  }
  try {
    console.info('[MG_CLIENT_VERSION_GUARD]', detail);
  } catch {
    // telemetry must never block app startup
  }
};

const removeVersionSensitiveLocalState = (): string[] => {
  if (!isBrowser()) return [];
  const removedKeys: string[] = [];
  try {
    for (const key of VERSION_SENSITIVE_LOCAL_KEYS) {
      if (window.localStorage.getItem(key) !== null) {
        removedKeys.push(key);
      }
      window.localStorage.removeItem(key);
    }
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      if (VERSION_SENSITIVE_LOCAL_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      window.localStorage.removeItem(key);
      removedKeys.push(key);
    }
  } catch {
    // ignore
  }
  return removedKeys;
};

const clearBrowserCaches = async (): Promise<string[]> => {
  if (!isBrowser() || !('caches' in window)) return [];
  try {
    const keys = await window.caches.keys();
    await Promise.all(keys.map((key) => window.caches.delete(key)));
    return keys;
  } catch {
    // ignore
  }
  return [];
};

const unregisterServiceWorkers = async (): Promise<number> => {
  if (!isBrowser() || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return 0;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
    return registrations.length;
  } catch {
    // ignore
  }
  return 0;
};

const cleanupVersionSensitiveState = async (): Promise<VersionCleanupResult> => {
  const localStorageKeysRemoved = removeVersionSensitiveLocalState();
  const [cacheStorageKeysRemoved, serviceWorkersUnregistered] = await Promise.all([
    clearBrowserCaches(),
    unregisterServiceWorkers(),
  ]);
  return {
    localStorageKeysRemoved,
    cacheStorageKeysRemoved,
    serviceWorkersUnregistered,
  };
};

const fetchServerBuildMetadata = async (): Promise<BuildMetadata | null> => {
  if (!isBrowser()) return null;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), VERSION_CHECK_TIMEOUT_MS);
  try {
    const response = await fetch(`/build.json?ts=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = await response.json() as Partial<BuildMetadata>;
    const buildSha = normalizeBuildSha(data.build_sha);
    if (buildSha === 'unknown') return null;
    return {
      build_sha: buildSha,
      build_time_utc: String(data.build_time_utc ?? ''),
      schema_version: Number(data.schema_version) || 1,
    };
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
};

const isServerBuildMismatch = (serverBuildSha: string): boolean => {
  const currentBuildSha = normalizeBuildSha(CURRENT_BUILD_SHA);
  const normalizedServerSha = normalizeBuildSha(serverBuildSha);
  return currentBuildSha !== 'unknown'
    && normalizedServerSha !== 'unknown'
    && normalizedServerSha !== currentBuildSha;
};

export const runClientVersionGuard = async (): Promise<ClientVersionGuardResult> => {
  if (!isBrowser()) {
    return { reloaded: false, currentBuildSha: normalizeBuildSha(CURRENT_BUILD_SHA) };
  }

  const currentBuildSha = normalizeBuildSha(CURRENT_BUILD_SHA);
  document.documentElement.dataset.buildSha = currentBuildSha;
  document.documentElement.dataset.buildTimeUtc = CURRENT_BUILD_TIME_UTC;

  const storedBuild = readStoredBuildState();
  let cleanupReason: string | undefined;
  if (storedBuild && storedBuild.build_sha !== currentBuildSha) {
    cleanupReason = `stored_build_changed:${storedBuild.build_sha}->${currentBuildSha}`;
    const cleanupResult = await cleanupVersionSensitiveState();
    emitVersionGuardTelemetry('cleanup', {
      reason: cleanupReason,
      from_build_sha: storedBuild.build_sha,
      to_build_sha: currentBuildSha,
      local_storage_keys_removed: cleanupResult.localStorageKeysRemoved,
      cache_storage_keys_removed: cleanupResult.cacheStorageKeysRemoved,
      service_workers_unregistered: cleanupResult.serviceWorkersUnregistered,
    });
  }

  writeStoredBuildState({
    build_sha: currentBuildSha,
    build_time_utc: CURRENT_BUILD_TIME_UTC,
    schema_version: 1,
  });

  const serverBuild = await fetchServerBuildMetadata();
  const serverBuildSha = serverBuild?.build_sha;
  if (serverBuild && isServerBuildMismatch(serverBuild.build_sha)) {
    const claim = claimReloadTarget(serverBuild.build_sha);
    if (claim.status !== 'claimed') {
      const cleanupReason = `server_build_mismatch_suppressed:${currentBuildSha}->${serverBuild.build_sha}:${claim.status}`;
      emitVersionGuardTelemetry('reload_suppressed', {
        reason: cleanupReason,
        from_build_sha: currentBuildSha,
        to_build_sha: serverBuild.build_sha,
        server_build_sha: serverBuild.build_sha,
        recovery_reason: claim.status,
      });
      return {
        reloaded: false,
        currentBuildSha,
        serverBuildSha,
        cleanupReason,
        recoveryRequired: true,
      };
    }
    cleanupReason = cleanupReason ?? `server_build_changed:${currentBuildSha}->${serverBuild.build_sha}`;
    const cleanupResult = await cleanupVersionSensitiveState();
    emitVersionGuardTelemetry('cleanup', {
      reason: cleanupReason,
      from_build_sha: currentBuildSha,
      to_build_sha: serverBuild.build_sha,
      server_build_sha: serverBuild.build_sha,
      local_storage_keys_removed: cleanupResult.localStorageKeysRemoved,
      cache_storage_keys_removed: cleanupResult.cacheStorageKeysRemoved,
      service_workers_unregistered: cleanupResult.serviceWorkersUnregistered,
    });
    emitVersionGuardTelemetry('reload_requested', {
      reason: cleanupReason,
      from_build_sha: currentBuildSha,
      to_build_sha: serverBuild.build_sha,
      server_build_sha: serverBuild.build_sha,
    });
    window.location.reload();
    return {
      reloaded: true,
      currentBuildSha,
      serverBuildSha,
      cleanupReason,
    };
  }

  return {
    reloaded: false,
    currentBuildSha,
    serverBuildSha,
    cleanupReason,
  };
};

const runClientVersionGuardDeduped = async (): Promise<ClientVersionGuardResult> => {
  if (!checkInFlight) {
    checkInFlight = runClientVersionGuard().finally(() => {
      checkInFlight = null;
    });
  }
  return checkInFlight;
};

export const installClientVersionWatch = (): void => {
  if (!isBrowser() || watchInstalled) return;
  watchInstalled = true;

  const check = () => {
    void runClientVersionGuardDeduped();
  };

  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
  window.setInterval(check, WATCH_INTERVAL_MS);
};
